import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { brandingAPI, resolveAssetUrl } from '../lib/api';
import { 
  LayoutDashboard,
  Calendar,
  QrCode,
  BarChart3,
  LogOut,
  Palette,
  Settings,
  Building2,
  Users,
  TrendingUp,
  Download,
  ScrollText
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, href: '/dashboard', key: 'dashboard' },
  { label: 'Events', icon: Calendar, href: '/events', key: 'events' },
  { label: 'Member Sponsorship', icon: Building2, href: '/member-sponsorship', key: 'member-sponsorship' },
  { label: 'Check-in', icon: QrCode, href: '/checkin', key: 'checkin' },
  { label: 'Reports', icon: BarChart3, href: '/reports', key: 'reports' },
  { label: 'Analytics', icon: TrendingUp, href: '/analytics', key: 'analytics' },
];

export default function Layout({ children, activeNav }) {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [canInstall, setCanInstall] = useState(false);
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    if (window.deferredPwaPrompt) setCanInstall(true);
    const handler = () => setCanInstall(true);
    window.addEventListener('pwa-installable', handler);
    return () => window.removeEventListener('pwa-installable', handler);
  }, []);

  useEffect(() => {
    brandingAPI.get().then((res) => setBranding(res.data)).catch(() => {});
  }, []);

  const triggerInstall = async () => {
    const promptEvent = window.deferredPwaPrompt;
    if (!promptEvent) return;
    promptEvent.prompt();
    await promptEvent.userChoice.catch(() => {});
    window.deferredPwaPrompt = null;
    setCanInstall(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img
              src={branding?.logo_url ? resolveAssetUrl(branding.logo_url) : '/iida-logo.png'}
              alt={branding?.organization_name || 'Illawarra Indian Doctors Association'}
              className="h-10 w-auto object-contain"
              data-testid="header-logo"
            />
            <div>
              <h1 className="font-semibold text-foreground">IIDA CPD Companion</h1>
              <p className="text-xs text-muted-foreground">Educational Events</p>
            </div>
          </Link>
          
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link key={item.label} to={item.href}>
                <Button 
                  variant={activeNav === item.key ? "secondary" : "ghost"} 
                  size="sm"
                  className="gap-2"
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Button>
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {canInstall && (
              <Button
                variant="outline"
                size="sm"
                onClick={triggerInstall}
                className="gap-2"
                data-testid="pwa-install-btn"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Install app</span>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="settings-menu">
                  <Settings className="w-4 h-4" />
                  <span className="hidden sm:inline">{admin?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/branding')} data-testid="branding-menu-item">
                  <Palette className="w-4 h-4 mr-2" />
                  Branding Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/sponsors')} data-testid="sponsor-catalogue-menu-item">
                  <Building2 className="w-4 h-4 mr-2" />
                  Sponsor Catalogue
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/admins')} data-testid="admins-menu-item">
                  <Users className="w-4 h-4 mr-2" />
                  Admins &amp; Roles
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/audit')} data-testid="audit-menu-item">
                  <ScrollText className="w-4 h-4 mr-2" />
                  Audit Log
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pb-20 md:pb-0">
        {children}
      </main>

      {/* Mobile Nav (bottom bar) */}
      <div
        className="fixed bottom-0 left-0 right-0 md:hidden bg-card border-t border-border z-50"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
      >
        <div className="flex justify-around items-stretch px-1 py-1">
          {navItems.slice(0, 5).map((item) => (
            <Link key={item.label} to={item.href} className="flex-1">
              <Button 
                variant="ghost" 
                size="sm"
                className={`w-full flex-col gap-0.5 h-auto py-2 px-1 ${activeNav === item.key ? 'text-primary' : 'text-muted-foreground'}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[10px] leading-tight">{item.label}</span>
              </Button>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
